import type { Card, Content, NavItem } from './content';

const es: Content = {
  langLabel: 'Idioma',
  menuLabel: 'Abrir menú',
  nav: [
    { label: '//RESULTADOS', href: '/#proof' },
    { label: '//PROCESO', href: '/#process' },
    { label: '//SERVICIOS', href: '/#services' },
    { label: '//CASOS', href: '/#work' },
    { label: '//MANAGEDOPS', href: '/#managedops' },
    { label: '//FAQ', href: '/#faq' },
  ] as NavItem[],
  navExtra: [
    { label: '//BLOG', href: '/blog' },
    { label: '//CARRERAS', href: '/careers-opportunity' },
  ] as NavItem[],
  navStatus: 'ACEPTANDO PROYECTOS',
  navContact: 'CONTACTO',
  likeSite: {
    prompt: '¿TE GUSTA ESTE SITIO?',
    thanks: 'GRACIAS',
  },

  hero: {
    eyebrow: '//INGENIERÍA DE IA FORWARD-DEPLOYED',
    titleA: 'IA QUE SÍ LLEGA A',
    titleB: 'PRODUCCIÓN',
    sub: 'Alio integra ingenieros senior dentro de tu equipo para llevar agentes de IA, RAG y automatizaciones a producción, y luego los mantiene funcionando. Diseñado para organizaciones reguladas y operativamente complejas.',
    subStrong: 'Seguro. Gobernado. Propiedad de tu equipo.',
    rotorTag: '//LO QUE EL EQUIPO ENTREGA',
    rotor: [
      ['AGENTES DE IA', 'EN PRODUCCIÓN'],
      ['RAG', 'EN TU STACK'],
      ['CUMPLIMIENTO', 'EN EL PIPELINE'],
      ['QA', 'EN CADA RELEASE'],
      ['GASTO CLOUD', '30-40% MENOS'],
    ] as [string, string][],
    rotorMeta: 'PROYECTO O RETAINER MANAGEDOPS',
    ctaPrimary: 'AGENDA UNA CONSULTA',
    ctaGhost: 'VER CASOS',
    meta: ['DESDE 2011', 'EE. UU. · MÉXICO · INDIA', 'HUMANOS EN EL CIRCUITO', 'PRODUCCIÓN, NO DEMOS'],
  },

  terminal: {
    title: 'alio engagement.log',
    chip: 'SESIÓN DE EJEMPLO',
    aria: 'Sesión de ejemplo de un proyecto con Alio',
  },

  proof: {
    title: 'RESULTADOS',
    chip: 'DE PROYECTOS REALES',
    sub: 'Cifras de proyectos entregados y casos de estudio publicados. Sin pilotos, sin proyecciones.',
    mainLabel: 'ACAPS · PLATAFORMA DE IA HUMANITARIA',
    mainLabelDim: 'agentes + RAG con pgvector',
    big: [
      { to: 70, suffix: '%', cap: 'MENOS TIEMPO DE ANALISTAS', tone: 'accent' },
      { to: 60, suffix: '%', cap: 'MENOR COSTO DE BÚSQUEDA', tone: 'plain' },
    ],
    bars: [
      { name: 'Pruebas automatizadas NinjaOne', value: 60, label: '~60%' },
      { name: 'Cumplimiento más rápido (Drata)', value: 60, label: '60%' },
      { name: 'Salida al mercado más rápida', value: 40, label: '40%' },
      { name: 'Ahorro cloud (FinOps)', value: 40, label: '30-40%' },
      { name: 'Ahorro en migración cloud', value: 35, label: '35%' },
    ],
    log: [
      { text: '· acaps: OpenSearch reemplazado por pgvector, RAG multilingüe', stat: '-60%', href: '/white-papers' },
      { text: '· ninjaone: automatización de ~1,000 pruebas de regresión, TDD', stat: '~60%', href: '/case-studies/ninjaone' },
      { text: '· usahs: sincronización clínica Salesforce + Jenzabar + Core', stat: 'EN VIVO', href: '/case-studies/usahs' },
    ],
    sideA: {
      chip: '⌘ PRESENCIA',
      label: 'desde 2011',
      to: 3,
      suffix: '',
      cap: 'PAÍSES · UN EQUIPO',
      rows: ['ARIZONA, EE. UU.', 'HERMOSILLO, MX', 'NOIDA, IN'],
    },
    sideB: {
      chip: '⌘ ALCANCE',
      label: 'industrias atendidas',
      to: 14,
      suffix: '+',
      cap: 'REGULADAS + OPERATIVAS',
      rows: ['SALUD · FINTECH · SEGUROS', 'EDTECH · SERVICIOS PÚBLICOS · MANUFACTURA'],
    },
  },

  clients: {
    title: 'CONFÍAN EN NOSOTROS',
    chip: '14 EQUIPOS',
    partnersLabel: 'socios',
    partners: 'AWS · MICROSOFT · SALESFORCE · OPENAI · DRATA · GITLAB · MONGODB COE · GOOGLE CLOUD · BRAINCODERS.IO · ARIZONA TECHNOLOGY COUNCIL',
  },

  process: {
    title: 'PROCESO',
    chip: '5 ETAPAS · 1 EQUIPO',
    sub: 'Entra un flujo de trabajo roto. Sale un sistema en producción que tu equipo controla. Cada etapa entrega evidencia a la siguiente.',
    steps: [
      { num: '01', tag: '//DESCUBRIR', title: 'INTENCIÓN → MAPA DE FLUJOS', body: 'Nos sentamos con tus operadores, analistas y directivos para encontrar dónde se rompe realmente el trabajo. El mapa va antes que el modelo.' },
      { num: '02', tag: '//INTEGRAR', title: 'MAPA → EQUIPO', body: 'Ingenieros senior de EE. UU., México e India se suman a tus dailies, tus repositorios y tus canales. Mismo horario, mismos objetivos.' },
      { num: '03', tag: '//CONSTRUIR', title: 'EQUIPO → SISTEMAS', body: 'Agentes, pipelines de RAG, integraciones e infraestructura cloud, entregados en sprints contra criterios de aceptación que tú apruebas.' },
      { num: '04', tag: '//VERIFICAR', title: 'SISTEMAS → EVIDENCIA', body: 'QA automatizado, evaluaciones, revisión de seguridad y mapeo de cumplimiento. Revisión humana donde el riesgo es real.' },
      { num: '05', tag: '//OPERAR', title: 'EVIDENCIA → CONTROL', body: 'Runbooks y transferencia de conocimiento para que tu equipo sea dueño del sistema. O mantennos con ManagedOps y lo seguimos mejorando.' },
    ],
    flow: ['descubrir', 'integrar', 'construir', 'verificar', 'operar'],
    flowEnd: 'repetir hasta que funcione en tu entorno',
  },

  services: {
    title: 'SERVICIOS',
    chip: 'IA PRIMERO · FULL STACK',
    sub: 'No un proveedor generalista. Equipos especializados en IA, cloud, seguridad y entrega, bajo un solo contrato.',
    cards: [
      { tag: '//PRINCIPAL', title: 'Ingeniería de IA forward-deployed', body: 'Ingenieros senior se integran con tus operadores, mapean los flujos rotos y llevan agentes, RAG y automatizaciones a producción. No demos que se quedan en el cajón.', href: '/solutions/ai-engineering', variant: 'primary' },
      { tag: '//OPERACIONES', title: 'Alio ManagedOps', body: 'Operaciones de ingeniería gestionadas, no una mesa de ayuda. Cloud, DevOps, QA, seguridad e IA, en mejora continua con un retainer mensual.', href: '/managedops' },
      { tag: '//CLOUD', title: 'Cloud y DevOps', body: 'AWS, Azure y GCP. CI/CD, infraestructura como código, observabilidad y FinOps que reduce 30-40% del gasto cloud.', href: '/services' },
      { tag: '//SEGURIDAD', title: 'Ciberseguridad y vCISO', body: 'Liderazgo en seguridad sin contratar a tiempo completo. Programas de riesgo, preparación de auditorías, planes de incidentes y revisión de proveedores.', href: 'https://vciso.alioit.com' },
      { tag: '//SALUD', title: 'HealthTech e interoperabilidad', body: 'Mapeo HL7 FHIR, SMART on FHIR y USCDI, con preparación HIPAA, SOC 2 y HITRUST desde el inicio.', href: 'https://healthtech.alioit.com' },
      { tag: '//QA', title: 'Automatización de QA', body: 'Selenium, Cypress, Playwright y Katalon. Generación de pruebas asistida por IA con revisión humana. Nada sale a producción a ciegas.', href: '/case-studies/ai-qa-automation' },
      { tag: '//CRM', title: 'Salesforce', body: 'Sales, Service y Marketing Cloud. Administradores y desarrolladores certificados para implementaciones, integraciones y migración de datos.', href: '/case-studies/salesforce' },
      { tag: '//DESARROLLO', title: 'Desarrollo de software', body: 'Apps web, móviles y cloud con React, Node.js, Java, Python, Go y .NET. Mentalidad de producto, listas para lanzar.', href: '/services' },
      { tag: '//MÁS', title: '+5 más', body: 'UX/UI · ingeniería de datos · Web3 · PLM para manufactura · reclutamiento →', href: '/services', variant: 'more' },
    ] as Card[],
  },

  stack: {
    title: 'STACK',
    chip: 'TU ENTORNO, NO EL NUESTRO',
    sub: 'Aprendemos tu plataforma en lugar de venderte una nueva. Si mueve tu negocio, podemos entregar sobre ella.',
    last: '+ TU STACK',
    note: '// integrar con los sistemas de registro, no rodearlos',
  },

  work: {
    title: 'CASOS',
    chip: 'CASOS DE ESTUDIO',
    sub: 'Entregados y funcionando. Entra para ver la arquitectura, el stack y el resultado.',
    cards: [
      { tag: '//IA HUMANITARIA', title: 'ACAPS', body: 'Plataforma de conocimiento con IA para inteligencia humanitaria. Agentes, RAG con pgvector y procesamiento LLM multilingüe.', stat: '-70% TIEMPO DE ANALISTAS', href: '/white-papers' },
      { tag: '//MSP · SAAS B2B', title: 'NinjaOne', body: 'Alianza de QA remoto que automatiza un programa de ~1,000 pruebas e impulsa el desarrollo guiado por pruebas.', stat: '~60% AUTOMATIZADO', href: '/case-studies/ninjaone' },
      { tag: '//EDUCACIÓN SUPERIOR', title: 'USAHS', body: 'Integración de prácticas clínicas y datos de estudiantes entre Salesforce, Jenzabar y Core. Menos conciliación manual.', stat: '3 SISTEMAS SINCRONIZADOS', href: '/case-studies/usahs' },
      { tag: '//EDTECH', title: 'Time4Learning', body: 'Plataforma educativa PreK-12. Soporte de aplicaciones, desarrollo web y móvil, UX/UI. Desde 2022.', stat: 'DESDE 2022', href: '/case-studies/time4learning' },
      { tag: '//FINTECH', title: 'Nerito', body: 'Plataforma de pagos para la población no bancarizada de México. Pagos móviles, depósitos y retiros sobre AWS serverless.', stat: 'SERVERLESS', href: '/case-studies/nerito' },
      { tag: '//QA CON IA', title: 'QA aumentado con IA', body: 'Modelo de QA con IA usando Katalon, Jira, QMetry y Copilot CLI. Scripts más rápidos, diagnóstico de fallas más rápido.', stat: 'IA + REVISIÓN HUMANA', href: '/case-studies/ai-qa-automation' },
    ] as Card[],
  },

  managedops: {
    title: 'MANAGEDOPS',
    chip: 'SEGURO · ESTABLE · OPTIMIZADO',
    sub: 'El lanzamiento es el inicio, no el final. ManagedOps mejora tu cloud, seguridad, QA e IA cada mes con un retainer basado en resultados.',
    plansLabel: '//PLANES MENSUALES',
    plansDim: 'definidos tras la evaluación',
    colPlan: 'PLAN',
    colScope: 'ALCANCE',
    colPrice: 'RETAINER',
    plans: [
      { name: 'ESSENTIALS', desc: 'Revisión mensual de operaciones, soporte de tickets, backlog de cloud, seguridad y QA', price: '$3.5K-$7K/MES' },
      { name: 'GROWTH', desc: 'Soporte CI/CD, FinOps, mantenimiento de QA, remediación de seguridad, revisiones ejecutivas', price: '$7.5K-$14K/MES' },
      { name: 'ENTERPRISE', desc: 'Operación integral de cloud, IA, QA, seguridad y cumplimiento, con liderazgo dedicado', price: '$15K-$30K+/MES' },
    ],
    plansFoot: '// el precio final depende del alcance de la plataforma, horas de soporte, requisitos de cumplimiento y SLAs de respuesta',
    vsLabel: '⌘ MSP TRADICIONAL → ALIO MANAGEDOPS',
    vs: [
      ['Mesa de ayuda', 'Operaciones de ingeniería'],
      ['Gestión de dispositivos', 'Cloud, DevOps, QA, IA, seguridad'],
      ['Tickets reactivos', 'Mejora continua'],
      ['Precio por usuario', 'Retainers basados en resultados'],
    ] as [string, string][],
    vsFoot: '// detalle completo en',
    areas: [
      { title: 'CLOUD Y DEVOPS GESTIONADOS', body: 'AWS, Azure, GCP. Monitoreo de CI/CD, mantenimiento de IaC, FinOps, observabilidad y soporte en producción.' },
      { title: 'CIBERSEGURIDAD Y vCISO', body: 'Asesoría, preparación de auditorías, planes de respuesta a incidentes, remediación de riesgos y revisión de proveedores.' },
      { title: 'SEGURIDAD HEALTHTECH', body: 'Evaluaciones de riesgo HIPAA, preparación SOC 2 y HITRUST, protección de PHI y capacitación en seguridad.' },
      { title: 'AUTOMATIZACIÓN DE QA', body: 'Mantenimiento de regresión, preparación de releases y diagnóstico de fallas. Pruebas generadas con IA, revisadas por humanos.' },
      { title: 'DESARROLLO DE AGENTES DE IA', body: 'Agentes de dominio, herramientas y pipelines de RAG con evaluaciones, controles de seguridad y flujos con humanos en el circuito.' },
      { title: 'OPERACIONES DE IA', body: 'Mantenimiento de modelos, prompts y RAG, gobierno de IA, y monitoreo de costo y desempeño entre proveedores.' },
    ],
    cta: 'AGENDA UNA EVALUACIÓN MANAGEDOPS',
  },

  principles: {
    title: 'PRINCIPIOS',
    chip: 'CÓMO TRABAJAMOS',
    rows: [
      { num: '01', title: 'PRODUCCIÓN, NO DEMOS', body: 'Si no está funcionando en tu entorno, no está terminado. Un piloto es un paso, nunca el entregable.' },
      { num: '02', title: 'HUMANOS EN EL CIRCUITO', body: 'La IA potencia a tu equipo; nunca reemplaza el criterio. Hay revisión humana donde el riesgo es real.' },
      { num: '03', title: 'CUMPLIMIENTO POR DEFECTO', body: 'Los controles HIPAA, SOC 2, ISO 27001 y GDPR se mapean mientras construimos... no se agregan la semana antes de la auditoría.' },
      { num: '04', title: 'TU EQUIPO ES EL DUEÑO', body: 'Documentación, runbooks y mentoría en cada proyecto. Diseñamos la transferencia desde el primer día.' },
    ],
  },

  faq: {
    title: 'FAQ',
    chip: '[ + ] EXPANDE',
    items: [
      { q: '¿Qué es la ingeniería de IA forward-deployed?', a: 'Ingenieros senior de Alio se integran en tu organización para descubrir los flujos de trabajo reales, y luego construyen y llevan agentes de IA, sistemas RAG y automatizaciones a tu entorno de producción, con gobierno humano en el circuito y transferencia de conocimiento para que tu equipo conserve el control. Disponible como proyecto o como servicio continuo de ManagedOps.' },
      { q: '¿En qué se diferencia de staff augmentation o de un MSP?', a: 'Staff augmentation te da personas. Un MSP te da una mesa de ayuda. Alio te da resultados: ingenieros senior responsables del descubrimiento, la entrega y la operación, por proyecto o con un retainer ManagedOps basado en resultados.' },
      { q: '¿Con qué industrias trabajan?', a: 'Salud, FinTech, seguros, EdTech y educación superior, plataformas SaaS y MSP, manufactura, minería, logística, servicios públicos, cooperativas eléctricas, streaming, ciberseguridad y organizaciones humanitarias sin fines de lucro.' },
      { q: '¿Cómo manejan la seguridad y el cumplimiento?', a: 'Asesoría vCISO, seguridad HealthTech, integración de seguridad con Microsoft Purview y AWS, y automatización de cumplimiento con Drata para SOC 2, ISO 27001, HIPAA y GDPR, cerca de 60% más rápida que un programa manual.' },
      { q: '¿Pueden trabajar junto a nuestro equipo interno?', a: 'Sí. Nos sumamos a tus ceremonias Scrum para ingeniería de IA, DevOps, automatización de QA, frontend, pipelines de datos y UX, u operamos de forma continua con un retainer ManagedOps.' },
      { q: '¿Cómo empieza un proyecto?', a: 'Con una llamada. Agenda una consulta general o una evaluación ManagedOps de 30 minutos. Definimos el alcance, asignamos a los ingenieros y comenzamos con el descubrimiento.' },
    ],
  },

  cta: {
    eyebrow: '//CONECTA',
    titleA: 'DEJA DE PILOTEAR',
    titleB: 'EMPIEZA A ENTREGAR',
    sub: 'Tráenos el flujo de trabajo que siempre se rompe. Nosotros traemos el equipo que lo arregla.',
    primary: 'AGENDA UNA CONSULTA',
    ghost: 'EVALUACIÓN MANAGEDOPS',
  },

  contact: {
    title: 'CONTACTO',
    chip: 'EE. UU. · MX · IN',
    sub: 'Cuéntanos qué se sigue rompiendo y dónde corre. Te respondemos con un plan, no con un discurso de ventas.',
    officesLabel: '//OFICINAS',
    regions: { usa: 'EE. UU.', mexico: 'MÉXICO', india: 'INDIA' },
    formLabel: '//BRIEF DEL PROYECTO',
    formDim: 'new_engagement.form',
    required: '* obligatorio',
    sending: 'ENVIANDO...',
    orBook: '// ¿prefieres hablar ya?',
    orBookLink: 'agenda una consulta →',
  },

  footer: {
    tag: 'TU EQUIPO REMOTO, PERO MÁS CERCA.',
    status: 'EE. UU. · MX · IN EN LÍNEA',
    cols: [
      { head: '//SERVICIOS', links: [
        { label: 'Ingeniería de IA', href: '/solutions/ai-engineering' },
        { label: 'ManagedOps', href: '/managedops' },
        { label: 'Todos los servicios', href: '/services' },
        { label: 'vCISO →', href: 'https://vciso.alioit.com' },
      ] },
      { head: '//EMPRESA', links: [
        { label: 'Empresa', href: '/company' },
        { label: 'Casos de estudio', href: '/case-studies' },
        { label: 'Carreras', href: '/careers-opportunity' },
        { label: 'Comunidad', href: '/community' },
      ] },
      { head: '//RECURSOS', links: [
        { label: 'Blog', href: '/blog' },
        { label: 'White papers', href: '/white-papers' },
        { label: 'Portafolio UI/UX', href: '/uiux-portfolio' },
        { label: 'Privacidad', href: '/privacy' },
      ] },
    ],
    legalA: '© 2026 ALIO IT SOLUTIONS LLC · ARIZONA · HERMOSILLO · NOIDA',
    legalB: '+1 (520) 261-1117',
  },
};

export default es;
